import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

const fallbackGuide1 = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='600' height='340'%3E%3Crect width='600' height='340' fill='%232a2a2a'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='serif' font-size='28' fill='%23555'%3EGuide%3C/text%3E%3C/svg%3E";
const fallbackGuide2 = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='600' height='340'%3E%3Crect width='600' height='340' fill='%23282a28'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='serif' font-size='28' fill='%23555'%3ECompare%3C/text%3E%3C/svg%3E";

const innerArticles = [
  {
    title: 'Jak wyszukiwać produkty w Kakobuy Spreadsheet',
    excerpt: 'Naucz się filtrować kategorie, sortować po cenie i znajdować najlepsze znaleziska w kilka sekund.',
    slug: 'jak-wyszukiwac',
  },
  {
    title: 'Czym jest QC – Quality Control i dlaczego jest ważny?',
    excerpt: 'Quality Control to proces kontroli jakości produktu przed wysyłką. Sprawdź jak działa w Kakobuy.',
    slug: 'co-to-jest-qc',
  },
  {
    title: 'Najlepsze linie logistyczne z Chin do Polski',
    excerpt: 'Porównanie czasów dostawy i cen przesyłek. Znajdź optymalną metodę wysyłki.',
    slug: 'linie-logistyczne',
  },
  {
    title: 'Jak rozpoznać dobrego sprzedawcę?',
    excerpt: 'Sprawdź rating, opcję zwrotów, czas wysyłki i opinie z prawdziwymi zdjęciami.',
    slug: 'dobry-sprzedawca',
  },
];

export default function SEOEditorial() {
  const { t } = useTranslation();

  return (
    <section className="py-16 md:py-24 bg-background-100">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex flex-col lg:flex-row lg:items-start gap-4 mb-12 md:mb-16">
          <div className="lg:w-1/3">
            <h2 className="font-heading text-2xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 leading-tight">
              {t('editorial.section_title')}
            </h2>
          </div>
          <div className="lg:w-2/3 lg:pt-2">
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
              {t('editorial.section_desc')}
            </p>
          </div>
        </div>

        {/* Top: image card + guide cards side by side */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
          {/* Image card with workspace image */}
          <div className="md:col-span-1 bg-foreground-900 rounded-2xl p-6 md:p-8 flex flex-col justify-between min-h-[320px] relative overflow-hidden">
            <div>
              <h3 className="font-heading text-xl md:text-2xl font-bold text-background-50 leading-snug">
                {t('editorial.workspace_title')}
              </h3>
              <p className="mt-4 text-sm text-foreground-300 leading-relaxed">
                {t('editorial.workspace_desc')}
              </p>
            </div>
            <div className="mt-6 relative h-40 rounded-xl overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20minimalist%20workspace%20with%20laptop%20displaying%20spreadsheet%20data%20on%20clean%20desk%20warm%20ambient%20lighting%20coffee%20cup%20stylish%20interior%20soft%20shadows%20European%20aesthetic%20editorial%20photography%20warm%20terracotta%20and%20cream%20tones%20professional%20yet%20cozy%20atmosphere%20high%20detail%20no%20text%20no%20logos%20clean%20composition&width=600&height=400&seq=editorial-workspace-v2&orientation=landscape"
                alt="Kakobuy Spreadsheet workspace"
                title="Kakobuy Spreadsheet – Jak korzystać"
                className="w-full h-full object-cover rounded-xl"
                onError={(e) => { (e.target as HTMLImageElement).src = fallbackGuide1; }}
              />
            </div>
          </div>

          {/* Article card 1 */}
          <Link
            to="/artykul/kakobuy-spreadsheet-przewodnik-2026"
            className="md:col-span-1 bg-background-50 rounded-2xl border border-background-200/60 p-6 md:p-8 flex flex-col group hover:border-primary-200/60 transition-all duration-300 cursor-pointer"
          >
            <span className="inline-flex items-center gap-1.5 self-start px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium">
              <i className="ri-article-line text-xs"></i>
              {t('editorial.label')}
            </span>
            <div className="mt-4 flex-1">
              <h3 className="font-heading text-lg md:text-xl font-bold text-foreground-900 group-hover:text-primary-600 transition-colors leading-snug">
                {t('editorial.title')}
              </h3>
              <p className="mt-3 text-sm text-foreground-500 leading-relaxed">
                {t('editorial.subtitle')}
              </p>
            </div>
            <div className="mt-5 flex items-center gap-2 text-sm font-semibold text-primary-500 group-hover:text-primary-600 transition-colors">
              <span>{t('editorial.cta')}</span>
              <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
            </div>
          </Link>

          {/* Article card 2 */}
          <Link
            to="/artykul/porownanie-spreadsheetow-2026"
            className="md:col-span-1 bg-background-50 rounded-2xl border border-background-200/60 p-6 md:p-8 flex flex-col group hover:border-secondary-200/60 transition-all duration-300 cursor-pointer"
          >
            <span className="inline-flex items-center gap-1.5 self-start px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium">
              <i className="ri-scales-3-line text-xs"></i>
              Porównanie
            </span>
            <div className="mt-4 flex-1">
              <h3 className="font-heading text-lg md:text-xl font-bold text-foreground-900 group-hover:text-secondary-600 transition-colors leading-snug">
                Kakobuy vs CSSBuy vs Superbuy vs Oopbuy
              </h3>
              <p className="mt-3 text-sm text-foreground-500 leading-relaxed">
                Szczegółowe porównanie czterech najpopularniejszych platform zakupowych z Chin. Sprawdź, który Spreadsheet najlepiej pasuje do Twoich potrzeb.
              </p>
            </div>
            <div className="mt-5 flex items-center gap-2 text-sm font-semibold text-secondary-500 group-hover:text-secondary-600 transition-colors">
              <span>Czytaj porównanie</span>
              <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
            </div>
          </Link>
        </div>

        {/* Bottom: guide article mini list */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {innerArticles.map((article) => (
            <Link
              key={article.slug}
              to={`/artykul/kakobuy-spreadsheet-przewodnik-2026#${article.slug}`}
              className="group p-5 rounded-xl bg-background-50 border border-background-200/40 hover:border-primary-200/60 hover:bg-background-50/80 transition-all duration-300 cursor-pointer"
            >
              <h4 className="text-sm font-bold text-foreground-900 group-hover:text-primary-600 transition-colors leading-snug">
                {article.title}
              </h4>
              <p className="mt-2 text-xs text-foreground-500 leading-relaxed line-clamp-3">
                {article.excerpt}
              </p>
              <div className="mt-3 flex items-center gap-1 text-xs font-semibold text-primary-500 group-hover:text-primary-600 transition-colors">
                <span>Czytaj więcej</span>
                <i className="ri-arrow-right-line text-xs group-hover:translate-x-0.5 transition-transform"></i>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}